import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { Search, PlusCircle, ShieldAlert, HeartHandshake, HandHeart, Mail, Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Toaster } from "@/components/ui/sonner";
import "@/App.css";
import { EmergencyMarquee } from "@/components/EmergencyMarquee";
import { LanguageToggle } from "@/components/LanguageToggle";
import SearchPage from "@/pages/SearchPage";
import ReportPage from "@/pages/ReportPage";
import CaseDetail from "@/pages/CaseDetail";
import HandoffForm from "@/pages/HandoffForm";
import SeekerForm from "@/pages/SeekerForm";
import SeekerDetail from "@/pages/SeekerDetail";
import AdminPage from "@/pages/AdminPage";
import HelpWall from "@/pages/HelpWall";

function Header({ offset }) {
  const { t } = useTranslation();
  return (
    <header className={`bg-white border-b border-neutral-200 sticky ${offset ? "top-9" : "top-0"} z-30`}>
      <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <HeartHandshake className="w-6 h-6 text-[#0066CC]" />
          <span className="font-heading font-bold text-lg tracking-tight text-neutral-900">{t("nav.brand")}</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link to="/admin" data-testid="admin-nav-link"
            className="text-xs font-semibold uppercase tracking-wider text-neutral-500 hover:text-[#0066CC] flex items-center gap-1">
            <ShieldAlert className="w-4 h-4" /> {t("nav.admin")}
          </Link>
        </div>
      </div>
    </header>
  );
}

function BottomNav() {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const active = (p) => pathname === p ? "text-[#0066CC]" : "text-neutral-500";
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-30 md:hidden">
      <div className="grid grid-cols-4 max-w-2xl mx-auto">
        <Link to="/" data-testid="bottomnav-search" className={`flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] ${active("/")}`}>
          <Search className="w-5 h-5" />
          <span className="text-xs font-semibold">{t("nav.search")}</span>
        </Link>
        <Link to="/buscar-familiar" data-testid="bottomnav-seek" className={`flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] ${active("/buscar-familiar")}`}>
          <HandHeart className="w-5 h-5" />
          <span className="text-xs font-semibold">{t("nav.seek")}</span>
        </Link>
        <Link to="/reportar" data-testid="bottomnav-report" className={`flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] ${active("/reportar")}`}>
          <PlusCircle className="w-5 h-5" />
          <span className="text-xs font-semibold">{t("nav.found")}</span>
        </Link>
        <Link to="/muro" data-testid="bottomnav-wall" className={`flex flex-col items-center justify-center py-2 gap-0.5 min-h-[56px] ${active("/muro")}`}>
          <Megaphone className="w-5 h-5" />
          <span className="text-xs font-semibold">{t("nav.wall")}</span>
        </Link>
      </div>
    </nav>
  );
}

function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-neutral-200 mt-8" data-testid="site-footer">
      <div className="max-w-2xl mx-auto px-4 py-6 flex items-start gap-2 text-xs text-neutral-500 leading-relaxed">
        <Mail className="w-4 h-4 shrink-0 mt-0.5 text-neutral-400" />
        <p>
          {t("footer.text")}{" "}
          <a href="mailto:melver@visionariatech.com" data-testid="support-email"
            className="text-[#0066CC] font-medium hover:underline">melver@visionariatech.com</a>
        </p>
      </div>
    </footer>
  );
}

function Shell({ children }) {
  const { pathname } = useLocation();
  const showMarquee = pathname === "/";
  return (
    <div className={`App min-h-screen pb-20 md:pb-8 flex flex-col ${showMarquee ? "pt-9" : ""}`}>
      {showMarquee && <EmergencyMarquee />}
      <Header offset={showMarquee} />
      <main className="max-w-2xl mx-auto px-4 w-full py-4 flex-1">{children}</main>
      <Footer />
      <BottomNav />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/" element={<Shell><SearchPage /></Shell>} />
        <Route path="/reportar" element={<Shell><ReportPage /></Shell>} />
        <Route path="/caso/:id" element={<Shell><CaseDetail /></Shell>} />
        <Route path="/caso/:id/entrega" element={<Shell><HandoffForm /></Shell>} />
        <Route path="/buscar-familiar" element={<Shell><SeekerForm /></Shell>} />
        <Route path="/busqueda/:id" element={<Shell><SeekerDetail /></Shell>} />
        <Route path="/admin" element={<Shell><AdminPage /></Shell>} />
        <Route path="/muro" element={<Shell><HelpWall /></Shell>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
